import { useTranslation } from "react-i18next";
import { FoodsSelectServerPagination, Typography } from "@foodpilot/foods";
import { FormControl, useTheme } from "@mui/material";
import { useDelayedSearch } from "src/utils/useDelayedSearch";
import { useGetAllProductReference } from "src/api/sure-api/productReference/useGetAllProductReference";
import { ProductReference } from "src/api/sure-api/productReference/useGetOneProductReference";
import { FoodsSelectTriggerAdapter } from "src/components/EntityForm/FoodsSelectTriggerAdapter";
import { formatFieldLabel } from "src/components/EntityForm/FoodsFieldPrimitives";

type ProductRefFieldProps = {
    productReference: ProductReference | null;
    setNewProductRef: (newRef: ProductReference | null) => void;
    appearance?: "default" | "product-v2";
    label?: string;
    placeholder?: string;
};
export const ProductRefField = (props: ProductRefFieldProps) => {
    const { setNewProductRef, productReference, appearance = "default", label, placeholder } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const { search, setSearch, delayedSearch } = useDelayedSearch();
    const { productsReference = [] } = useGetAllProductReference({
        name: delayedSearch,
    });

    const isSearchFound = (value: string) => {
        if (delayedSearch === "") {
            return true;
        }

        return value.toLowerCase().includes(delayedSearch.toLowerCase());
    };

    const uniqueReferences = productsReference.filter((i) => i.id !== productReference?.id);
    if (productReference !== null) {
        uniqueReferences.push(productReference);
    }
    const selectedProduct = uniqueReferences.find((option) => option.id === productReference?.id) ?? null;
    const resolvedLabel = formatFieldLabel(label ?? t("produit_associe", "Associated product"), label === undefined);
    const resolvedPlaceholder = placeholder ?? t("component.select.placeholder", "Select an option");
    const select = (
        <FoodsSelectServerPagination
            onChange={(data) => {
                if (data === null) {
                    return setNewProductRef(null);
                }
                setNewProductRef(data);
            }}
            options={uniqueReferences}
            selectedOption={selectedProduct}
            getId={(item) => item.id}
            getName={(item) => item.name}
            textOptions={{
                placeholder: resolvedPlaceholder,
                searchPlaceholder: t("component.select.searchplaceholder", "Search…"),
                emptyOptionsText: placeholder ?? t("component.select.nooptions", "No option available"),
                defaultValueText: t("component.select.defaultvalue", "Default value"),
                noResult: t("component.select.noresultfound", "No results found"),
            }}
            search={search}
            setSearch={setSearch}
            isSearchFound={isSearchFound}
            BoxProps={{ width: "100%" }}
            BoxOptionsProps={
                appearance === "product-v2" ?
                    { width: theme.spacing(56.25) }
                :   { minWidth: theme.spacing(37.5), width: "50%" }
            }
        />
    );

    return (
        <FormControl size="small">
            <Typography variant="body-semibold" sx={{ paddingBottom: "0.5rem" }}>
                {resolvedLabel}
            </Typography>
            {appearance === "product-v2" ?
                <FoodsSelectTriggerAdapter accessibleLabel={resolvedLabel}>{select}</FoodsSelectTriggerAdapter>
            :   select}
        </FormControl>
    );
};
